import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Building2, Mail, Lock, Phone, ArrowRight, CheckCircle, Clock, Shield, Users } from 'lucide-react';

export default function PruebaGratisPage() {
    const [form, setForm] = useState({ nombre_empresa: '', email: '', password: '', telefono: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const res = await api.post('/tenants/register', form);
            login(res.data.access_token, res.data.user);
            // Fetch full user info with tenant data
            try {
                const token = res.data.access_token;
                const meRes = await api.get('/auth/me', { headers: { Authorization: `Bearer ${token}` } });
                login(token, meRes.data);
            } catch (e) { /* fallback to registration data */ }
            navigate('/');
        } catch (err) {
            setError(err.response?.data?.detail || 'Error al registrar');
        } finally {
            setLoading(false);
        }
    };

    const features = [
        { icon: Users, text: 'Gestión completa de clientes y préstamos' },
        { icon: Shield, text: 'Análisis crediticio con scoring automático' },
        { icon: Clock, text: 'Control de mora y cobros inteligente' },
        { icon: CheckCircle, text: 'Reportes, PDFs y contabilidad integrada' },
    ];

    return (
        <div data-testid="prueba-gratis-page" className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 flex items-center justify-center p-4">
            <div className="w-full max-w-5xl grid md:grid-cols-2 gap-0 bg-slate-900/80 rounded-2xl overflow-hidden border border-slate-700/50 shadow-2xl">
                {/* Left - Benefits */}
                <div className="p-8 md:p-12 bg-gradient-to-br from-indigo-600/20 to-slate-900/40 flex flex-col justify-center">
                    <div className="mb-2">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-semibold tracking-wide uppercase">
                            <Clock className="w-3.5 h-3.5" />
                            15 días gratis
                        </span>
                    </div>
                    <h1 className="text-3xl md:text-4xl font-bold text-white mb-3 leading-tight">
                        PrestaGest<span className="text-indigo-400">SaaS</span>
                    </h1>
                    <p className="text-slate-300 mb-8 text-base">
                        El sistema de gestión de préstamos más completo. Empiece a gestionar su cartera hoy mismo.
                    </p>
                    <div className="space-y-4">
                        {features.map((f, i) => (
                            <div key={i} className="flex items-start gap-3">
                                <div className="w-8 h-8 rounded-lg bg-indigo-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <f.icon className="w-4 h-4 text-indigo-400" />
                                </div>
                                <span className="text-slate-200 text-sm">{f.text}</span>
                            </div>
                        ))}
                    </div>
                    <p className="text-slate-500 text-xs mt-8">Sin tarjeta de crédito. Sin compromiso.</p>
                </div>

                {/* Right - Form */}
                <div className="p-8 md:p-12 flex flex-col justify-center">
                    <h2 className="text-xl font-semibold text-white mb-6">Crear cuenta de prueba</h2>
                    
                    {error && (
                        <div data-testid="register-error" className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="text-sm text-slate-400 mb-1 block">Nombre de la empresa</label>
                            <div className="relative">
                                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                                <input
                                    data-testid="register-empresa"
                                    type="text"
                                    required
                                    value={form.nombre_empresa}
                                    onChange={e => setForm({...form, nombre_empresa: e.target.value})}
                                    className="w-full pl-10 pr-4 py-2.5 bg-slate-800/80 border border-slate-600/50 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/30 transition-colors"
                                    placeholder="Mi Empresa de Préstamos"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="text-sm text-slate-400 mb-1 block">Email</label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                                <input
                                    data-testid="register-email"
                                    type="email"
                                    required
                                    value={form.email}
                                    onChange={e => setForm({...form, email: e.target.value})}
                                    className="w-full pl-10 pr-4 py-2.5 bg-slate-800/80 border border-slate-600/50 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/30 transition-colors"
                                    placeholder="admin@miempresa.com"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="text-sm text-slate-400 mb-1 block">Contraseña</label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                                <input
                                    data-testid="register-password"
                                    type="password"
                                    required
                                    minLength={6}
                                    value={form.password}
                                    onChange={e => setForm({...form, password: e.target.value})}
                                    className="w-full pl-10 pr-4 py-2.5 bg-slate-800/80 border border-slate-600/50 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/30 transition-colors"
                                    placeholder="Mínimo 6 caracteres"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="text-sm text-slate-400 mb-1 block">Teléfono (opcional)</label>
                            <div className="relative">
                                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                                <input
                                    data-testid="register-telefono"
                                    type="tel"
                                    value={form.telefono}
                                    onChange={e => setForm({...form, telefono: e.target.value})}
                                    className="w-full pl-10 pr-4 py-2.5 bg-slate-800/80 border border-slate-600/50 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/30 transition-colors"
                                    placeholder="809-555-0000"
                                />
                            </div>
                        </div>
                        <button
                            data-testid="register-submit-btn"
                            type="submit"
                            disabled={loading}
                            className="w-full py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-medium rounded-lg flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                        >
                            {loading ? (
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                                <>
                                    Iniciar prueba gratis
                                    <ArrowRight className="w-4 h-4" />
                                </>
                            )}
                        </button>
                    </form>
                    <p className="text-center text-sm text-slate-500 mt-6">
                        ¿Ya tiene cuenta?{' '}
                        <Link to="/login" className="text-indigo-400 hover:text-indigo-300 transition-colors">
                            Iniciar sesión
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
